function normalizeCurrencyCode(input) {
    if (input && typeof input === 'object') {
        return String(input.code ?? '').trim().toUpperCase() || null;
    }
    const code = String(input ?? '').trim().toUpperCase();
    return code || null;
}

const LANGUAGE_CURRENCY_BY_CODE = {
    bg: 'BGN',
    cs: 'CZK',
    cz: 'CZK',
    de: 'EUR',
    en: 'GBP',
    es: 'EUR',
    fr: 'EUR',
    hr: 'EUR',
    hu: 'HUF',
    it: 'EUR',
    nl: 'EUR',
    pl: 'PLN',
    ro: 'RON',
    sk: 'EUR',
    sl: 'EUR',
};

function normalizeLanguageCode(lang) {
    return String(lang?.code ?? '').trim().toLowerCase().split(/[-_]/)[0] || null;
}

function currencyCodeFromLanguage(lang) {
    const direct = normalizeCurrencyCode(
        lang?.currency?.code
        ?? lang?.default_currency?.code
        ?? lang?.currency_code
        ?? lang?.currencyCode
        ?? lang?.default_currency_code
        ?? lang?.defaultCurrencyCode
    );
    if (direct) return direct;

    const langCode = normalizeLanguageCode(lang);
    return langCode ? LANGUAGE_CURRENCY_BY_CODE[langCode] || null : null;
}

function isCurrencyAvailable(list, code) {
    const normalized = normalizeCurrencyCode(code);
    return !!normalized && list.some((item) => normalizeCurrencyCode(item) === normalized);
}

function persistCurrencyLanguageMarker(lang) {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const langCode = normalizeLanguageCode(lang);
    if (langCode) localStorage.setItem('currencyLang', langCode);
}

export const currencyModule = {
    namespaced: true,
    state: {
        selectedCurrencyCode: null
    },
    getters: {
        currentCurrency: (state, getters, rootState) => {
            const list = Array.isArray(rootState?.currencies) ? rootState.currencies : [];
            const selectedCode = String(state.selectedCurrencyCode ?? '').trim().toUpperCase();
            const selected = list.find((item) => String(item?.code ?? '').trim().toUpperCase() === selectedCode);
            const fallback = selected || list[0] || {};

            return {
                ...fallback,
                code: String(fallback?.code ?? '').trim().toUpperCase(),
                symbol: String(fallback?.symbol ?? '').trim(),
                conversion: Number(fallback?.conversion ?? fallback?.rate ?? 1) || 1
            };
        }
    },
    mutations: {
        SET_CURRENCY(state, currency) {
            const code = normalizeCurrencyCode(currency);
            state.selectedCurrencyCode = code;

            if (typeof window !== 'undefined' && window.localStorage) {
                if (code) {
                    localStorage.setItem('currency', code);
                } else {
                    localStorage.removeItem('currency');
                }
            }
        },
        SET_CURRENCY_CODE(state, code) {
            const normalizedCode = normalizeCurrencyCode(code);
            state.selectedCurrencyCode = normalizedCode;

            if (typeof window !== 'undefined' && window.localStorage) {
                if (normalizedCode) {
                    localStorage.setItem('currency', normalizedCode);
                } else {
                    localStorage.removeItem('currency');
                }
            }
        }
    },
    actions: {
        initCurrency({ commit, rootState }) {
            const list = Array.isArray(rootState?.currencies) ? rootState.currencies : [];
            const firstCode = normalizeCurrencyCode(list[0]);
            const currentLangCode = normalizeLanguageCode(rootState?.lang);
            const langCurrencyCode = currencyCodeFromLanguage(rootState?.lang);
            let storedCode = null;
            let storedLangCode = null;

            if (typeof window !== 'undefined' && window.localStorage) {
                const raw = localStorage.getItem('currency');
                if (raw) {
                    storedCode = normalizeCurrencyCode(raw);
                }
                storedLangCode = normalizeLanguageCode({ code: localStorage.getItem('currencyLang') });
            }

            if (
                currentLangCode
                && currentLangCode !== storedLangCode
                && isCurrencyAvailable(list, langCurrencyCode)
            ) {
                commit('SET_CURRENCY_CODE', langCurrencyCode);
                persistCurrencyLanguageMarker(rootState?.lang);
                return;
            }

            const isValidStored = isCurrencyAvailable(list, storedCode);
            if (isValidStored) {
                commit('SET_CURRENCY_CODE', storedCode);
                persistCurrencyLanguageMarker(rootState?.lang);
                return;
            }

            commit('SET_CURRENCY_CODE', firstCode);
            persistCurrencyLanguageMarker(rootState?.lang);
        },

        setCurrencyByCode({ commit, rootState }, code) {
            const targetCode = String(code ?? '').trim().toUpperCase();
            const list = Array.isArray(rootState?.currencies) ? rootState.currencies : [];
            const firstCode = normalizeCurrencyCode(list[0]);
            const found = list.find((item) => normalizeCurrencyCode(item) === targetCode);
            commit('SET_CURRENCY_CODE', found ? targetCode : firstCode);
            persistCurrencyLanguageMarker(rootState?.lang);
        }
    }
};
